<template>
  <div class="pageRoot linkListPage collectionModule">
    <div class="pageShell" ref="pageCard">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <app-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="应用"
                    prop="name"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.name || '—' }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="Icon"
                    prop="icon"
                    align="center"
                    header-align="center"
                    width="88"
                  >
                    <template slot-scope="scope">
                      <img
                        v-if="scope.row.icon"
                        v-lazy="scope.row.icon"
                        alt=""
                        class="appIconCell"
                      />
                      <span v-else class="cellMuted">—</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="包名"
                    prop="packageList"
                    align="center"
                    header-align="center"
                    min-width="220"
                  >
                    <template slot-scope="scope">
                      <div v-if="scope.row.packageList && scope.row.packageList.length" class="packageCell">
                        <span
                          v-for="(item, index) in scope.row.packageList"
                          :key="`${scope.row.id}-${index}-${item}`"
                          class="packageCell__item"
                          :title="item"
                        >
                          {{ item }}
                        </span>
                      </div>
                      <span v-else class="cellMuted">—</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="平台"
                    prop="system"
                    align="center"
                    header-align="center"
                    width="100"
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="'typeTag--' + getPlatformVariant(scope.row.system)"
                      >
                        {{ getPlatformLabel(scope.row.system) }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="更新时间"
                    prop="updateTime"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="200"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="lookInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                        <el-button
                          v-if="showActions"
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-edit"
                          v-check-role
                          @click.stop="openEdit(scope.row.id)"
                        >
                          编辑
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-mobile-phone" /></div>
                <p class="tableEmpty__title">暂无应用</p>
                <p class="tableEmpty__desc">调整筛选条件或新增应用。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="$router.push('/channel/app/create')"
                >
                  新增应用
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <app-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getAppList } from '../../../api/appManage'
import AppListFilterPanel from './AppListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import { getPlatformLabel, getPlatformVariant } from './appListHelpers'

export default {
  name: 'AppManageList',
  components: {
    AppListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      appOrBundleId: '',
      platform: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
    }
  },
  computed: {
    showActions() {
      return this.$route.meta.role == 2
    },
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.appOrBundleId, this.platform].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.appOrBundleId || '').trim()) {
        chips.push({
          key: 'appOrBundleId',
          label: '应用名/包名',
          value: this.appOrBundleId.trim(),
        })
      }
      if (String(this.platform || '').trim()) {
        chips.push({
          key: 'platform',
          label: '平台',
          value: getPlatformLabel(this.platform),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        appOrBundleId: this.appOrBundleId,
        platform: this.platform,
        activeFilterCount: this.activeFilterCount,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.$router.push('/channel/app/create'),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:appOrBundleId': (value) => {
          this.appOrBundleId = value
        },
        'update:platform': (value) => {
          this.platform = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getList()
  },
  methods: {
    getPlatformLabel,
    getPlatformVariant,
    getList() {
      this.loading = true
      getAppList({
        currentPage: this.currentPage,
        currentPageSize: this.currentPageSize,
        appOrBundleId: this.appOrBundleId,
        platform: this.platform,
      })
        .then((res) => {
          if (res.success) {
            this.total = res.total
            this.tableData = res.data || []
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.appOrBundleId = ''
      this.platform = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    goCreate() {
      this.$router.push('/channel/app/create')
    },
    lookInfo(id) {
      this.$router.push(`/channel/app/info/${id}`)
    },
    openEdit(id) {
      this.$router.push(`/channel/app/edit/${id}`)
    },
  },
}
</script>

<style scoped>
.appIconCell {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  vertical-align: middle;
  box-shadow: var(--link-shadow-card, 0 2px 8px rgba(28, 25, 23, 0.08));
}

.packageCell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 72px;
  overflow: auto;
  text-align: left;
}

.packageCell__item {
  display: block;
  font-family: var(--link-font-mono, ui-monospace, monospace);
  font-size: var(--link-text-xs);
  color: var(--link-ink-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cellMuted {
  color: var(--link-ink-muted);
}
</style>
